/**
 * useSelectionKeyboardShortcuts Hook
 *
 * Provides keyboard shortcuts for component selection management in the editor.
 *
 * Shortcuts:
 * - Shift+Click: Multi-select components (toggle selection)
 * - Delete/Backspace: Delete selected components
 * - Cmd/Ctrl+C: Copy selected components to clipboard
 * - Escape: Clear selection
 * - Cmd/Ctrl+A: Select all components
 *
 * Smart Focus Detection:
 * - Does not trigger when user is typing in input fields, textareas, or contenteditable elements
 * - Prevents browser default behaviors appropriately
 *
 * Week 3: Selection System implementation
 */

import { useEffect, useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { devLog } from '@/utils/devLog';
import { toast } from 'sonner';

interface UseSelectionKeyboardShortcutsOptions {
  /**
   * Enable or disable the keyboard shortcuts
   * Useful for temporarily disabling when in certain modes
   */
  enabled?: boolean;

  /**
   * Callback fired when shortcuts are triggered
   * Useful for analytics or debugging
   */
  onShortcutTriggered?: (shortcut: string, selectedCount: number) => void;
}

/**
 * Check if the user is currently typing in an input field
 * We don't want to trigger shortcuts while typing
 */
const isInputFocused = (): boolean => {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea';
  const isContentEditable = (activeElement as HTMLElement).isContentEditable;

  return isInput || isContentEditable;
};

const hasActiveTextSelection = (): boolean => {
  const { textSelection } = useEditorStore.getState();
  return Boolean(
    textSelection &&
    textSelection.componentId &&
    textSelection.startIndex !== textSelection.endIndex
  );
};

/**
 * useSelectionKeyboardShortcuts Hook
 *
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * function EditorCanvas() {
 *   // Enable keyboard shortcuts
 *   useSelectionKeyboardShortcuts({
 *     enabled: true,
 *     onShortcutTriggered: (shortcut, count) => {
 *       console.log(`Triggered ${shortcut} on ${count} components`);
 *     }
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useSelectionKeyboardShortcuts(
  options: UseSelectionKeyboardShortcutsOptions = {}
) {
  const { enabled = true, onShortcutTriggered } = options;

  const {
    selectedComponents,
    deleteSelectedComponents,
    copySelectedComponents,
    clearSelection,
    selectAllComponents,
  } = useEditorStore();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when disabled
      if (!enabled) return;

      // Don't trigger shortcuts when user is editing text inline
      const { textEditingComponentId } = useEditorStore.getState();
      if (textEditingComponentId !== null) return;

      // Don't trigger component deletion when a text range is selected
      if (hasActiveTextSelection()) {
        devLog('KEYBOARD', 'Shortcuts disabled: active text selection');
        return;
      }

      // Don't trigger shortcuts when user is typing in an input
      if (isInputFocused()) {
        devLog('KEYBOARD', 'Shortcuts disabled: input focused');
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? event.metaKey : event.ctrlKey;

      // Delete/Backspace: Delete selected components
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedComponents.length > 0) {
          event.preventDefault();

          devLog('KEYBOARD', 'Delete shortcut triggered', {
            selectedCount: selectedComponents.length,
          });

          deleteSelectedComponents();

          toast.success(`Deleted ${selectedComponents.length} component${selectedComponents.length === 1 ? '' : 's'}`);

          onShortcutTriggered?.('delete', selectedComponents.length);
        }
        return;
      }

      // Cmd/Ctrl+C: Copy selected components
      if (ctrlOrCmd && event.key === 'c') {
        if (selectedComponents.length > 0) {
          event.preventDefault();

          devLog('KEYBOARD', 'Copy shortcut triggered', {
            selectedCount: selectedComponents.length,
          });

          copySelectedComponents();

          toast.success(`Copied ${selectedComponents.length} component${selectedComponents.length === 1 ? '' : 's'}`);

          onShortcutTriggered?.('copy', selectedComponents.length);
        }
        return;
      }

      // Escape: Clear selection
      if (event.key === 'Escape') {
        if (selectedComponents.length > 0) {
          event.preventDefault();

          devLog('KEYBOARD', 'Escape shortcut triggered', {
            previousSelectedCount: selectedComponents.length,
          });

          clearSelection();

          onShortcutTriggered?.('clear-selection', selectedComponents.length);
        }
        return;
      }

      // Cmd/Ctrl+A: Select all components
      if (ctrlOrCmd && event.key === 'a') {
        event.preventDefault();

        devLog('KEYBOARD', 'Select all shortcut triggered');

        selectAllComponents();

        // Get count after selection
        const allCount = useEditorStore.getState().selectedComponents.length;
        toast.success(`Selected all ${allCount} component${allCount === 1 ? '' : 's'}`);

        onShortcutTriggered?.('select-all', allCount);
        return;
      }
    },
    [
      enabled,
      selectedComponents,
      deleteSelectedComponents,
      copySelectedComponents,
      clearSelection,
      selectAllComponents,
      onShortcutTriggered,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    devLog('KEYBOARD', 'Selection keyboard shortcuts enabled');

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      devLog('KEYBOARD', 'Selection keyboard shortcuts disabled');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  // Return selected components count for convenience
  return {
    selectedCount: selectedComponents.length,
    hasSelection: selectedComponents.length > 0,
  };
}

/**
 * Helper hook to temporarily disable shortcuts
 *
 * Useful for modals, dialogs, or other contexts where
 * you don't want selection shortcuts to trigger
 *
 * @example
 * ```tsx
 * function MyModal() {
 *   // Disable shortcuts while modal is open
 *   useDisableSelectionShortcuts();
 *
 *   return <Dialog>...</Dialog>;
 * }
 * ```
 */
export function useDisableSelectionShortcuts() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Prevent all selection shortcuts
      const isShortcut =
        e.key === 'Delete' ||
        e.key === 'Backspace' ||
        e.key === 'Escape' ||
        ((e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'a'));

      if (isShortcut && !isInputFocused()) {
        e.stopPropagation();
        devLog('KEYBOARD', 'Shortcut blocked (disabled context)');
      }
    };

    window.addEventListener('keydown', handler, true); // Capture phase

    return () => {
      window.removeEventListener('keydown', handler, true);
    };
  }, []);
}

/**
 * List of all available keyboard shortcuts
 * Useful for documentation or help screens
 */
export const SELECTION_SHORTCUTS = {
  multiSelect: {
    keys: ['Shift+Click'],
    description: 'Toggle component selection (multi-select)',
    mac: 'Shift+Click',
    windows: 'Shift+Click',
  },
  delete: {
    keys: ['Delete', 'Backspace'],
    description: 'Delete selected components',
    mac: 'Delete or ⌫',
    windows: 'Delete or Backspace',
  },
  copy: {
    keys: ['Cmd+C', 'Ctrl+C'],
    description: 'Copy selected components to clipboard',
    mac: '⌘C',
    windows: 'Ctrl+C',
  },
  clearSelection: {
    keys: ['Escape'],
    description: 'Clear selection',
    mac: 'Esc',
    windows: 'Esc',
  },
  selectAll: {
    keys: ['Cmd+A', 'Ctrl+A'],
    description: 'Select all components',
    mac: '⌘A',
    windows: 'Ctrl+A',
  },
} as const;

export type SelectionShortcut = keyof typeof SELECTION_SHORTCUTS;
